/**
 * PoolDestinationPicker — saisir une destination ET la géolocaliser (2026-09-14).
 * =============================================================================
 * Avant ce composant, la destination d'une réservation était un champ texte
 * libre : « Mairie de Blagnac » ne dit rien à une carte. Le panneau de garde
 * « Voir sur la carte » ne pouvait donc jamais placer le point d'arrivée — il
 * montrait le véhicule, jamais où il allait.
 *
 * Le composant garde le texte libre (c'est ce que LIT le conducteur, et un
 * gestionnaire doit pouvoir écrire « chantier RD820, après le pont ») et lui
 * adjoint un point optionnel, posé par trois gestes :
 *   1. recherche d'adresse sur la Base Adresse Nationale (api-adresse.data.gouv.fr,
 *      GET public, sans clé) ;
 *   2. un « lieu fréquent » — les unités territoriales passées en `sites` ;
 *   3. un clic sur la carte (ou l'épingle qu'on fait glisser).
 *
 * ——— Ce qui est délibéré ————————————————————————————————————————————————————
 * - Le point est OPTIONNEL. Une destination sans coordonnées reste valide : on
 *   le dit en gris sous le champ, on ne le reproche pas. La démo CD31 du 17/09
 *   doit pouvoir enchaîner sans poser un point à chaque réservation.
 * - Le libellé n'est pas verrouillé par la recherche : choisir une adresse le
 *   PRÉ-REMPLIT, l'utilisateur reste libre de le réécrire. Le libellé sert
 *   l'humain, les coordonnées servent la carte, les deux n'ont pas à coïncider.
 * - L'indisponibilité réseau de la BAN est une ligne calme, pas une erreur
 *   rouge : la carte reste parfaitement utilisable sans elle.
 *
 * ——— Limites connues ————————————————————————————————————————————————————————
 * - La BAN ne couvre que la France. Hors de France, seul le clic carte pose un
 *   point.
 * - Aucun géocodage INVERSE : un point posé au clic ne fabrique pas de libellé.
 *   C'est volontaire — un libellé inventé par la machine serait lu comme une
 *   adresse vérifiée.
 * - Les tuiles OSM sont chargées depuis l'extérieur : hors réseau, la carte
 *   reste cliquable mais grise. Le point posé, lui, est correct.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { MapPin, Search, X } from 'lucide-react';
// Import d'EFFET : `MapWrapper` corrige au chargement les icônes Leaflet par
// défaut (`L.Icon.Default.mergeOptions`). Sans lui, l'épingle est invisible.
import '../map/MapWrapper.jsx';

/** Toulouse — la préfecture du département de notre premier client public. */
const FALLBACK_CENTER = { lat: 43.6045, lng: 1.4440 };
const DEFAULT_ZOOM = 11;
const PIN_ZOOM = 14;

/** En dessous, la BAN rend du bruit : on n'interroge pas. */
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;
const BAN_URL = 'https://api-adresse.data.gouv.fr/search/';

const EMPTY_VALUE = { label: '', lat: null, lng: null };

const formatCoords = (lat, lng) =>
  `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;

const isNumber = (n) => typeof n === 'number' && Number.isFinite(n);

/** Les unités territoriales n'ont pas toutes des coordonnées : celles-là ne sont pas des raccourcis. */
function siteToPlace(site) {
  // `Number(null)` vaut 0 : sans ce filtre, une U.T. sans GPS se proposerait au
  // large du golfe de Guinée, et personne ne le verrait.
  if (site.gps_latitude === null || site.gps_latitude === undefined || site.gps_latitude === '') return null;
  if (site.gps_longitude === null || site.gps_longitude === undefined || site.gps_longitude === '') return null;
  const lat = Number(site.gps_latitude);
  const lng = Number(site.gps_longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { id: site.id, label: site.site_name || `#${site.id}`, lat, lng };
}

/** Le clic carte pose ou déplace l'épingle — un seul geste, pas de mode à armer. */
function MapClickCatcher({ onPick }) {
  useMapEvents({
    click: (e) => {
      if (e?.latlng) onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/**
 * Recentre SEULEMENT sur un choix explicite (résultat de recherche, lieu
 * fréquent). Recentrer aussi au clic carte ferait fuir la vue sous le curseur.
 */
function Recenter({ lat, lng, token }) {
  const map = useMap();
  useEffect(() => {
    if (!token || !map || typeof map.setView !== 'function') return;
    if (!isNumber(lat) || !isNumber(lng)) return;
    map.setView([lat, lng], PIN_ZOOM);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/**
 * @param {Object} props
 * @param {{ label: string, lat: number|null, lng: number|null }} [props.value]
 * @param {(next: { label: string, lat: number|null, lng: number|null }) => void} props.onChange
 * @param {Array<object>} [props.sites] - unités territoriales (`site_name`, `gps_latitude`, `gps_longitude`)
 */
function PoolDestinationPicker({ value, onChange, sites = [] }) {
  const { t } = useTranslation();
  const current = value || EMPTY_VALUE;
  const [mapOpen, setMapOpen] = useState(false);

  const hasPin = isNumber(current.lat) && isNumber(current.lng);

  const inputClass =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div>
      <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        <MapPin className="h-3.5 w-3.5" />
        {t('poolDestination.fieldLabel')}
      </span>
      <div className="flex gap-2">
        <input
          type="text"
          value={current.label || ''}
          onChange={(e) => onChange({ ...current, label: e.target.value })}
          placeholder={t('poolDestination.placeholder')}
          aria-label={t('poolDestination.fieldLabel')}
          className={`${inputClass} flex-1`}
        />
        <button
          type="button"
          onClick={() => setMapOpen(true)}
          className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
        >
          {t('poolDestination.chooseOnMap')}
        </button>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {hasPin
          ? t('poolDestination.pinSet', { coords: formatCoords(current.lat, current.lng) })
          : t('poolDestination.notGeocoded')}
      </p>

      {mapOpen && (
        <DestinationMapModal
          value={current}
          sites={sites}
          onCancel={() => setMapOpen(false)}
          onConfirm={(next) => {
            setMapOpen(false);
            onChange(next);
          }}
        />
      )}
    </div>
  );
}

function DestinationMapModal({ value, sites, onCancel, onConfirm }) {
  const { t } = useTranslation();

  const [label, setLabel] = useState(value.label || '');
  const [lat, setLat] = useState(isNumber(value.lat) ? value.lat : null);
  const [lng, setLng] = useState(isNumber(value.lng) ? value.lng : null);
  const [recenterToken, setRecenterToken] = useState(0);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchDown, setSearchDown] = useState(false);

  const places = useMemo(() => sites.map(siteToPlace).filter(Boolean), [sites]);

  // Centre initial : l'épingle, sinon le premier lieu fréquent, sinon Toulouse.
  const initialCenter = useRef(null);
  if (initialCenter.current === null) {
    if (isNumber(value.lat) && isNumber(value.lng)) initialCenter.current = { lat: value.lat, lng: value.lng };
    else if (places.length > 0) initialCenter.current = { lat: places[0].lat, lng: places[0].lng };
    else initialCenter.current = FALLBACK_CENTER;
  }
  const initialZoom = isNumber(value.lat) && isNumber(value.lng) ? PIN_ZOOM : DEFAULT_ZOOM;

  // Échap ferme SANS rien changer : une carte ouverte par curiosité ne doit pas
  // écraser une destination déjà saisie.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Recherche BAN, débouncée. La requête précédente est abandonnée : sinon une
  // réponse lente écrase une frappe récente.
  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setSearching(false);
      setSearched(false);
      return undefined;
    }
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      fetch(`${BAN_URL}?q=${encodeURIComponent(q)}&limit=6`, controller ? { signal: controller.signal } : undefined)
        .then((res) => {
          if (!res || !res.ok) throw new Error('BAN unavailable');
          return res.json();
        })
        .then((body) => {
          if (cancelled) return;
          const features = Array.isArray(body?.features) ? body.features : [];
          setResults(
            features
              .map((f, i) => {
                // La BAN rend GeoJSON : [lng, lat] — l'inverse de Leaflet.
                const coords = f?.geometry?.coordinates;
                if (!Array.isArray(coords) || coords.length < 2) return null;
                return {
                  id: f.properties?.id || `ban-${i}`,
                  label: f.properties?.label || '',
                  lat: Number(coords[1]),
                  lng: Number(coords[0]),
                };
              })
              .filter((r) => r && isNumber(r.lat) && isNumber(r.lng))
          );
          setSearchDown(false);
          setSearched(true);
          setSearching(false);
        })
        .catch((err) => {
          if (cancelled || err?.name === 'AbortError') return;
          setResults([]);
          setSearchDown(true);
          setSearched(true);
          setSearching(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (controller) controller.abort();
    };
  }, [query]);

  const pickPlace = useCallback((place) => {
    setLat(place.lat);
    setLng(place.lng);
    if (place.label) setLabel(place.label);
    setRecenterToken((n) => n + 1);
  }, []);

  const movePin = useCallback((nextLat, nextLng) => {
    setLat(nextLat);
    setLng(nextLng);
  }, []);

  const hasPin = isNumber(lat) && isNumber(lng);

  const inputClass =
    'w-full rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="max-h-[90vh] w-[640px] max-w-full overflow-y-auto rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-xl bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-white" />
            <h3 className="text-base font-semibold text-white">{t('poolDestination.modal.title')}</h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-white/80 hover:text-white"
            aria-label={t('poolDestination.modal.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3 p-5">
          {/* Recherche d'adresse — Base Adresse Nationale */}
          <div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('poolDestination.modal.searchPlaceholder')}
                aria-label={t('poolDestination.modal.searchLabel')}
                className={inputClass}
              />
            </div>
            {searching && <p className="mt-1 text-xs text-gray-400">{t('poolDestination.modal.searching')}</p>}
            {searchDown && (
              <p className="mt-1 text-xs text-gray-500">{t('poolDestination.modal.searchUnavailable')}</p>
            )}
            {!searching && !searchDown && searched && results.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">{t('poolDestination.modal.noResults')}</p>
            )}
            {results.length > 0 && (
              <p className="mt-1 text-xs text-gray-400">
                {t('poolDestination.modal.resultCount', { count: results.length })}
              </p>
            )}
            {results.length > 0 && (
              <ul className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-gray-200">
                {results.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => pickPlace(r)}
                      className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-blue-50"
                    >
                      {r.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Lieux fréquents — les unités territoriales, en un clic */}
          {places.length > 0 && (
            <div>
              <span className="text-xs font-medium text-gray-600">{t('poolDestination.modal.frequentPlaces')}</span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {places.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickPlace(p)}
                    className="rounded-full border border-gray-300 bg-gray-50 px-2.5 py-1 text-xs text-gray-700 hover:bg-blue-50"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Carte — cliquer pose l'épingle, la glisser la déplace */}
          <div className="h-[300px] overflow-hidden rounded-lg border border-gray-200">
            <MapContainer
              center={[initialCenter.current.lat, initialCenter.current.lng]}
              zoom={initialZoom}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapClickCatcher onPick={movePin} />
              <Recenter lat={lat} lng={lng} token={recenterToken} />
              {hasPin && (
                <Marker
                  position={[lat, lng]}
                  draggable
                  eventHandlers={{
                    dragend: (e) => {
                      const pos = e?.target?.getLatLng?.();
                      if (pos) movePin(pos.lat, pos.lng);
                    },
                  }}
                />
              )}
            </MapContainer>
          </div>
          <p className="text-xs text-gray-400">{t('poolDestination.modal.mapHint')}</p>

          {/* Libellé — ce que le conducteur lira */}
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">
              {t('poolDestination.modal.labelField')}
            </span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('poolDestination.placeholder')}
              aria-label={t('poolDestination.modal.labelField')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>

          <div className="flex items-center justify-between border-t border-gray-200 pt-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {hasPin
                  ? t('poolDestination.modal.coordinates', { coords: formatCoords(lat, lng) })
                  : t('poolDestination.modal.noPin')}
              </span>
              {hasPin && (
                <button
                  type="button"
                  onClick={() => {
                    setLat(null);
                    setLng(null);
                  }}
                  className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                >
                  {t('poolDestination.modal.clearPin')}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                {t('poolDestination.modal.cancel')}
              </button>
              <button
                type="button"
                onClick={() => onConfirm({ label, lat: hasPin ? lat : null, lng: hasPin ? lng : null })}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
              >
                {t('poolDestination.modal.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PoolDestinationPicker;
